import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useResolucaoQuestoes from "./UseResolucaoQuestoes";
import { usePageBlockStore } from "../store/usePageBlockStore";
import useQuestionFiltersStore, {
  ActiveFilterToken,
  AnswerStatus,
  FilterDimension,
  countPendingChanges,
  countSelection,
  selectionTokens,
} from "../store/useQuestionFiltersStore";

/**
 * Filter behaviour for the question bank: draft state (from the store) plus the
 * side effects that need React context — committing the query to redux, firing
 * the search, and navigating to the carrossel once it lands.
 *
 * Selection is batched, not live (§23.11): toggling a chip only changes the
 * draft. Nothing reaches the API until apply() runs.
 */
export default function useQuestionFilters() {
  const { setFilter, buscarPaginada, filterQuery } = useResolucaoQuestoes();
  const setBlocking = usePageBlockStore((s) => s.setBlocking);

  const draft = useQuestionFiltersStore((s) => s.draft);
  const applied = useQuestionFiltersStore((s) => s.applied);
  const hydrated = useQuestionFiltersStore((s) => s.hydrated);
  const applying = useQuestionFiltersStore((s) => s.applying);
  const panelOpen = useQuestionFiltersStore((s) => s.panelOpen);

  const hydrate = useQuestionFiltersStore((s) => s.hydrate);
  const toggle = useQuestionFiltersStore((s) => s.toggle);
  const remove = useQuestionFiltersStore((s) => s.remove);
  const setAnswerStatus = useQuestionFiltersStore((s) => s.setAnswerStatus);
  const clearAll = useQuestionFiltersStore((s) => s.clearAll);
  const markApplied = useQuestionFiltersStore((s) => s.markApplied);
  const setApplying = useQuestionFiltersStore((s) => s.setApplying);
  const setPanelOpen = useQuestionFiltersStore((s) => s.setPanelOpen);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const activeFilters: ActiveFilterToken[] = useMemo(
    () => selectionTokens(draft),
    [draft],
  );

  const selectedCount = useMemo(() => countSelection(draft), [draft]);
  const pendingChanges = useMemo(
    () => countPendingChanges(draft, applied),
    [draft, applied],
  );

  const apply = useCallback(async () => {
    const current = useQuestionFiltersStore.getState().draft;

    setApplying(true);
    setBlocking(true);

    // Await the committed filter: buscaPaginada reads filterQuery back off the
    // store, so dispatching both in the same tick would search the old query.
    await setFilter({
      ...filterQuery,
      years: current.years,
      agencyNames: current.agencies,
      subjects: current.subjects,
      keywords: current.keywords,
      answerStatus: current.answerStatus,
      // Superseded by the three split dimensions above. Sent as an empty list
      // rather than omitted so a stale value can never survive in the store.
      keySubjectAgency: [],
      page: 1,
      limit: 10,
    });

    markApplied();
    buscarPaginada();
  }, [filterQuery, setFilter, buscarPaginada, setBlocking, markApplied, setApplying]);

  return {
    draft,
    applied,
    hydrated,
    applying,
    panelOpen,
    activeFilters,
    selectedCount,
    pendingChanges,
    hasPendingChanges: pendingChanges > 0,

    toggle,
    remove,
    setAnswerStatus,
    clearAll,
    setPanelOpen,
    apply,
  };
}

export type { ActiveFilterToken, AnswerStatus, FilterDimension };

/**
 * Completion side effects for apply(). Mount this exactly once — FilterBar owns
 * it — otherwise the navigation fires once per mounted copy.
 */
export function useQuestionFilterApplyEffect() {
  const navigate = useNavigate();
  const { fetching } = useResolucaoQuestoes();
  const applying = useQuestionFiltersStore((s) => s.applying);
  const setApplying = useQuestionFiltersStore((s) => s.setApplying);
  const setPanelOpen = useQuestionFiltersStore((s) => s.setPanelOpen);
  const wasFetching = useRef(false);

  useEffect(() => {
    if (wasFetching.current && !fetching && applying) {
      setApplying(false);
      setPanelOpen(false);
      navigate("/carrossel");
      setTimeout(() => usePageBlockStore.getState().setBlocking(false), 0);
    }
    wasFetching.current = fetching;
  }, [fetching, applying, navigate, setApplying, setPanelOpen]);
}
